<script>
import {LayerAreasSelectable} from "@/libs/Layers";
import {VariableConfiguration} from "@/libs/VisualizationData";

export default {
  emits: ['onLayerUpdated'],
  props: {
    propId: String,
    propTitle1: String,
    propTitle2: String,
    propTitle3: String,
    propTitle3Object: Object,

    //Configuration of variable for selected layer
    propLayerVariableConfiguration: {type: VariableConfiguration, default: null},

    //List of layer and available areas
    propLayerAreasSelectable: {type: LayerAreasSelectable, default: null},

    //Hovered object to show info
    propLayerHoverFeatureId: {type: Number, default: null},

    propToggle: Boolean,
    propShowMunicipalities: {type: Boolean, default: false},
    propAreaMultipleSelection: Boolean
  },
  watch: {
    propToggle(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('onLayerUpdated')
      }
    }
  },
  computed: {
    modelData() {
      let modelData = {};
      let altitudeValue = null;
      let municipalities = null;

      if (this.hasHoveredOrSelectedArea === true) {
        if (this.propLayerHoverFeatureId !== null) {
          altitudeValue = this.propLayerAreasSelectable.featureAltitudeRound(this.propLayerHoverFeatureId);
          municipalities = this.propLayerAreasSelectable.featureMunicipalities(this.propLayerHoverFeatureId);
        } else {
          altitudeValue = this.propLayerAreasSelectable.layersSelectedAltitudeAverage;
        }
      }

      if (altitudeValue !== null)
        modelData['WorksInputs.Altitude'] = {
          label: `${altitudeValue} ${this.$t('Units.AboveSeaLevel')}`,
          usePill: true
        }

      if (this.propShowMunicipalities === true && municipalities !== null)
        modelData['SimulationInputs.Municipalities'] = {
          label: municipalities,
          usePill: false
        }

      return modelData;
    },
    hasHoveredOrSelectedArea() {
      return this.hoverAreaIsSet ||
          (this.propLayerAreasSelectable !== null && this.propLayerAreasSelectable.layersSelectedCount > 0);
    },
    hoverAreaIsSet() {
      return this.propLayerHoverFeatureId !== null
    },
    hoverAreaValue() {
      return this.hoverAreaIsSet && this.propLayerAreasSelectable !== null ?
          this.propLayerAreasSelectable.featureValueUnitStr(this.propLayerHoverFeatureId, this.$i18n) :
          '';
    },
    hoverAreaName() {
      return this.hoverAreaIsSet && this.propLayerAreasSelectable !== null ?
          this.propLayerAreasSelectable.featureAreaName(this.propLayerHoverFeatureId) :
          '';
    },
  },
  methods: {
    itemsCountSelected() {
      return this.propLayerAreasSelectable !== null ? this.propLayerAreasSelectable.layersSelectedCount : 0;
    },
    itemsCountTotal() {
      return this.propLayerAreasSelectable !== null ? this.propLayerAreasSelectable.layersCount : 0;
    },
    areaOneSelectedName() {
      return this.propLayerAreasSelectable !== null ? this.propLayerAreasSelectable.layerSelectedOneName : '';
    },
    areasManySelectedAverage() {
      let valueStr = '';
      if (this.propLayerAreasSelectable !== null) {
        let value = this.propLayerAreasSelectable.layersSelectedValueAverage;
        if (value !== null) {
          valueStr = `${value}`;
          if (this.propLayerVariableConfiguration !== null && this.propLayerVariableConfiguration.unit !== null)
            valueStr = `${valueStr} ${this.propLayerVariableConfiguration.unit}`
        }
      }
      return valueStr;
    }
  },
  updated() {
    this.$emit('onLayerUpdated')
  }
}
</script>

<template>
  <div :id="propId" class="control-layer control-layer-info bg-white-opacity-80">
    <!-- Layer information -->
    <div class="row" v-if="(propTitle1 !== null && propTitle1 !== '') || (propTitle2 !== null && propTitle2 !== '')">
      <div class="col">
        <span class="h6 text-primary" v-if="propTitle1 !== null && propTitle1 !== ''">{{ propTitle1 }}</span>
        <span class="fw-bold ms-1" v-if="propTitle2 !== null && propTitle2 !== ''">{{ propTitle2 }}</span>
      </div>
    </div>
    <div class="row" v-if="propTitle3 !== null || propTitle3Object !== null">
      <div class="col">
        <template v-if="propTitle3 !== null && propTitle3 !== ''">
          <span class="fw-light ms-1">{{ propTitle3 }}</span>
        </template>
        <template v-if="propTitle3Object !== null">
          <span class="fw-light">
            <template v-for="(value, name) in propTitle3Object">
              <span class="badge rounded-pill bg-secondary me-1">{{ name }}</span><span class="me-1">{{ value }}</span>
            </template>
          </span>
        </template>
      </div>
    </div>

    <!-- Information for hovered area / selected areas -->
    <div class="row mt-2 mb-2 text-center">
      <div class="col">
        <template v-if="this.hasHoveredOrSelectedArea === true">
          <table class="table table-sm table-responsive table-bg-white-opacity-0 m-0 me-1">
            <thead>
            <tr>
              <!-- Area name -->
              <td class="text-start">
                <span class="fw-semibold text-dark title">
                  <template v-if="hoverAreaIsSet === true">
                    {{ hoverAreaName }}
                  </template>
                  <template v-else>
                    <template v-if="itemsCountSelected() === 1">
                      {{ areaOneSelectedName() }}
                    </template>
                    <template v-else>
                      {{ $t('InputSearchFormLabelValue.MeanSelectedAreas') }}
                    </template>
                  </template>
                </span>
              </td>

              <!-- Area value -->
              <td class="text-end">
                <span class="badge rounded-pill text-bg-purple table-value">
                  <template v-if="hoverAreaIsSet === true">
                    {{ hoverAreaValue }}
                  </template>
                  <template v-else>
                    {{ areasManySelectedAverage() }}
                  </template>
                </span>
              </td>
            </tr>
            </thead>

            <!-- Attributes -->
            <tbody>
            <template v-for=" (value, key) in modelData">
              <tr>
                <td class="text-start table-key">
                  <span class="me-1">{{ $t(key) }}</span>
                </td>
                <td class="text-end">
                  <span :class="{
                    'badge rounded-pill text-bg-purple table-value': value.usePill === true,
                    'small': value.usePill === false
                  }">
                  {{ value.label }}
                  </span>
                </td>
              </tr>
            </template>
            </tbody>
          </table>
        </template>
        <template v-else>
          <p class="fst-italic lh-1 m-0 p-0 my-1">
            <span class="me-1">{{ $t('layerInfo.hoverData') }}</span>
            <span v-html="$t('layerInfo.clickTimeSeries')"></span>
          </p>
        </template>
      </div>
    </div>
    <div class="row text-end text-secondary">
      <div class="col" v-if="propAreaMultipleSelection === true">
        <span class="fw-bold">{{ itemsCountSelected() }}/{{ itemsCountTotal() }}</span>
        {{ $tc('layerInfo.selectedElements', itemsCountSelected()) }}
      </div>
      <div class="col" v-else>
        <span>{{ $t('layerInfo.clickDataOnlyOne') }}</span>
      </div>
    </div>
  </div>
</template>